/**
 * SiteManager — calendar/agenda view per site (Phase F α).
 *
 * Route: /site-manager
 *
 * Differs from Dock Manager (horizontal time-strip): this is a vertical
 * **agenda** view — choose a site + day, see all bookings as a chronological
 * list, grouped by bay. Toggle between Day / Week.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import { listSitesLite, listBaysLite } from "@/lib/api";
import { useViewAsClient } from "@/lib/viewAsClient";
import { supabase } from "@/lib/supabaseClient";
import { statusBadgeClass } from "@/lib/constants";
import { formatInSiteTz } from "@/lib/datetime";
import { toast } from "sonner";
import SiteDocumentsLink from "@/components/SiteDocumentsLink";

const VIEWS = [
  { value: "day",  label: "Day" },
  { value: "week", label: "Week" },
];

export default function SiteManager() {
  const { viewAsClientId } = useViewAsClient();
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [bays, setBays]     = useState([]);
  const [view, setView]     = useState("day");
  const [anchor, setAnchor] = useState(() => new Date());
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading]   = useState(false);

  const selectedSite = useMemo(() => sites.find((s) => s.id === siteId), [sites, siteId]);

  // Compute range based on view + anchor
  const range = useMemo(() => {
    const start = new Date(anchor); start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    if (view === "day") end.setDate(end.getDate() + 1);
    else end.setDate(end.getDate() + 7);
    return { start, end };
  }, [anchor, view]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await listSitesLite();
      if (cancelled) return;
      const filtered = (data || []).filter((s) => !viewAsClientId || s.client_id === viewAsClientId);
      setSites(filtered);
      if (filtered.length) setSiteId((cur) => cur || filtered[0].id);
    })();
    return () => { cancelled = true; };
  }, [viewAsClientId]);

  useEffect(() => {
    let cancelled = false;
    if (!siteId) return () => { cancelled = true; };
    listBaysLite(siteId, { includeInactive: true }).then(({ data }) => {
      if (!cancelled) setBays(data || []);
    });
    return () => { cancelled = true; };
  }, [siteId]);

  // Load bookings for site + range
  useEffect(() => {
    if (!siteId) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("lbs_bookings")
        .select(`
          id, reference_number, status, direction,
          scheduled_start, scheduled_end,
          bay_id, sender_company, receiver_company, vehicle_type,
          external_ref, internal_ref,
          bay:lbs_bays!lbs_bookings_bay_id_fkey(id,name,color)
        `)
        .eq("site_id", siteId)
        .gte("scheduled_start", range.start.toISOString())
        .lt("scheduled_start", range.end.toISOString())
        .order("scheduled_start", { ascending: true });
      if (cancelled) return;
      if (error) toast.error(error.message);
      setBookings(data || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [siteId, range.start, range.end]);

  const byBay = useMemo(() => {
    const m = new Map();
    for (const b of bays) m.set(b.id, []);
    for (const bk of bookings) {
      if (!m.has(bk.bay_id)) m.set(bk.bay_id, []);
      m.get(bk.bay_id).push(bk);
    }
    return m;
  }, [bookings, bays]);

  const tz = selectedSite?.timezone;

  const nudge = (delta) => {
    setAnchor((a) => {
      const next = new Date(a);
      next.setDate(next.getDate() + delta * (view === "day" ? 1 : 7));
      return next;
    });
  };

  return (
    <div className="space-y-4" data-testid="site-manager-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Site manager</h1>
          <p className="text-sm text-slate-500">Agenda view of all bookings at a site — by day or by week.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => nudge(-1)} data-testid="sm-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setAnchor(new Date())} data-testid="sm-today">Today</Button>
          <Button variant="outline" size="sm" onClick={() => nudge(1)} data-testid="sm-next"><ChevronRight className="h-4 w-4" /></Button>
          <SiteDocumentsLink siteId={siteId} siteName={selectedSite?.name} />
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <div>
            <label className="text-xs text-slate-500">Site</label>
            <Select value={siteId} onValueChange={setSiteId}>
              <SelectTrigger data-testid="sm-site"><SelectValue placeholder="Pick a site" /></SelectTrigger>
              <SelectContent>
                {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500">View</label>
            <Select value={view} onValueChange={setView}>
              <SelectTrigger data-testid="sm-view"><SelectValue /></SelectTrigger>
              <SelectContent>
                {VIEWS.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="text-sm text-slate-600 flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            {view === "day"
              ? range.start.toLocaleDateString("en-AU", { weekday: "long", year: "numeric", month: "long", day: "numeric" })
              : `${range.start.toLocaleDateString("en-AU")} – ${new Date(range.end - 1).toLocaleDateString("en-AU")}`}
          </div>
        </CardContent>
      </Card>

      {/* Agenda — one card per bay */}
      {!siteId ? (
        <div className="text-sm text-slate-500">Pick a site to see the agenda.</div>
      ) : loading ? (
        <div className="text-sm text-slate-400">Loading…</div>
      ) : bays.length === 0 ? (
        <div className="text-sm text-slate-500">No bays at this site yet.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {bays.map((bay) => {
            const list = byBay.get(bay.id) || [];
            return (
              <Card key={bay.id} data-testid={`sm-bay-card-${bay.id}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span className="h-3 w-3 rounded-full" style={{ background: bay.color }} />
                    {bay.name}
                    {!bay.is_active && (
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-200 text-slate-600" data-testid={`sm-bay-inactive-${bay.id}`}>
                        inactive
                      </span>
                    )}
                    <span className="text-xs text-slate-500 font-normal ml-auto">{list.length} bookings</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {list.length === 0 ? (
                    <div className="text-xs text-slate-400 italic">No bookings.</div>
                  ) : (
                    <ul className="space-y-2">
                      {list.map((bk) => (
                        <li key={bk.id} className="flex items-start justify-between gap-3 text-sm border-b last:border-0 pb-2 last:pb-0">
                          <div>
                            <div className="font-mono text-xs text-slate-500">{bk.reference_number}</div>
                            <div className="font-medium">
                              {tz ? formatInSiteTz(bk.scheduled_start, tz, { hour: "2-digit", minute: "2-digit" }) : new Date(bk.scheduled_start).toLocaleTimeString("en-AU", { hourCycle: "h23" })}
                              {" – "}
                              {tz ? formatInSiteTz(bk.scheduled_end,   tz, { hour: "2-digit", minute: "2-digit" }) : new Date(bk.scheduled_end).toLocaleTimeString("en-AU", { hourCycle: "h23" })}
                            </div>
                            <div className="text-xs text-slate-600">
                              {bk.direction} · {bk.sender_company || "—"} → {bk.receiver_company || "—"}
                            </div>
                            {(bk.external_ref || bk.internal_ref) && (
                              <div className="text-xs text-slate-400">
                                {bk.external_ref && <>Ext: {bk.external_ref}</>}
                                {bk.external_ref && bk.internal_ref && " · "}
                                {bk.internal_ref && <>Int: {bk.internal_ref}</>}
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${statusBadgeClass(bk.status)}`}>{bk.status}</span>
                            <Link to={`/bookings?id=${bk.id}`} className="text-xs text-blue-600 hover:underline" data-testid={`sm-open-${bk.id}`}>Open</Link>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
